import { Table, Avatar, Tag, Dropdown, Button } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useMemo, useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PendingActivity } from "../PendingActivities/PendingActivities";
import { collabStartChat } from "services/SauceService";
import {
  faEnvelope,
  faMapMarkerAlt,
  faBell,
  faUser,
  faBullhorn,
  faCalendarAlt,
  faEye,
  faPaperPlane,
  faTimes,
  faUtensils,
  faDollarSign,
  faMessage,
  faBan,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { ColumnsType } from "antd/es/table";
import { InviteStatusEnum } from "enums/InviteStatusEnum";
import "./InvitationsTable.scss";

export enum InviteType {
  Personal = "personal",
  Campaign = "campaign",
}

interface InvitationsTableProps<T = any> {
  dataSource?: T[];
  loading?: boolean;
  showCreatorInfo?: boolean;
  onCancel?: (invite: PendingActivity) => void;
  className?: string;
  fetch?: (
    limit: number,
    offset: number
  ) => Promise<{
    data: T[];
    total: number;
  }>;
}

const InvitationsTable = <T extends Record<string, any>>({
  dataSource: externalDataSource,
  loading: externalLoading = false,
  showCreatorInfo = false,
  onCancel,
  className = "invitations-table",
  fetch: fetchData,
}: InvitationsTableProps<T>) => {
  const navigate = useNavigate();
  const scroll = { x: 850 };
  const [dataSource, setDataSource] = useState<T[]>(externalDataSource || []);
  const [loading, setLoading] = useState<boolean>(externalLoading);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    if (fetchData) {
      setPagination((prev) => ({ ...prev, current: 1 }));
    }
  }, [fetchData]);

  useEffect(() => {
    if (fetchData) {
      setLoading(true);
      fetchData(
        pagination.pageSize,
        (pagination.current - 1) * pagination.pageSize
      )
        .then((result) => {
          setDataSource(result.data || []);
          setPagination((prev) => ({
            ...prev,
            total: result.total || 0,
          }));
        })
        .catch((err) => {
          console.error("Failed to fetch invitations", err);
          setDataSource([]);
          setPagination((prev) => ({ ...prev, total: 0 }));
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (externalDataSource !== undefined) {
      setDataSource(externalDataSource);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData, pagination.current, pagination.pageSize, externalDataSource]);

  useEffect(() => {
    setLoading(externalLoading);
  }, [externalLoading]);

  const handlePaginationChange = useCallback(
    (page: number, pageSize: number) => {
      setPagination((prev) => ({
        ...prev,
        current: page,
        pageSize: pageSize,
      }));
    },
    []
  );

  const paginationConfig = fetchData
    ? {
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        showSizeChanger: false,
        showTotal: (total: number, range: [number, number]) => (
          <p className="pagination-total-text">
            Showing{" "}
            <b>
              {range[0]}-{range[1]}
            </b>{" "}
            of <b>{total}</b> creators
          </p>
        ),
        onChange: handlePaginationChange,
        onShowSizeChange: handlePaginationChange,
      }
    : {
        pageSize: 10,
        showSizeChanger: false,
        showTotal: (total: number, range: [number, number]) => (
          <p className="pagination-total-text">
            Showing{" "}
            <b>
              {range[0]}-{range[1]}
            </b>{" "}
            of <b>{total}</b> creators
          </p>
        ),
      };

  const getStatusConfig = (status: InviteStatusEnum | null | undefined) => {
    const statusValue = status ?? InviteStatusEnum.Sent;
    switch (statusValue) {
      case InviteStatusEnum.Booked:
        return {
          label: "Booked",
          color: "#16a34a",
          bgColor: "#dcfce7",
          icon: faCalendarAlt,
        };
      case InviteStatusEnum.Viewed:
        return {
          label: "Viewed",
          color: "#9333ea",
          bgColor: "#f3e8ff",
          icon: faEye,
        };
      case InviteStatusEnum.Rejected:
        return {
          label: "Rejected",
          color: "#991b1b",
          bgColor: "#fee2e2",
          icon: faBan,
        };
      case InviteStatusEnum.Sent:
        return {
          label: "Sent",
          color: "#2563eb",
          bgColor: "#dbeafe",
          icon: faPaperPlane,
        };
      case InviteStatusEnum.Expired:
        return {
          label: "Expired",
          color: "#dc2626",
          bgColor: "#fee2e2",
          icon: faTimes,
        };
      default:
        return {
          label: "Sent",
          color: "#2563eb",
          bgColor: "#dbeafe",
          icon: faPaperPlane,
        };
    }
  };

  const getInviteTypeConfig = (type: InviteType | number) => {
    switch (type) {
      case InviteType.Personal:
        return {
          label: "Personal",
          color: "#9333ea",
          bgColor: "#f3e8ff",
          icon: faUser,
        };
      case InviteType.Campaign:
        return {
          label: "Campaign",
          color: "#2563eb",
          bgColor: "#dbeafe",
          icon: faBullhorn,
        };
      default:
        return {
          label: "Personal",
          color: "#9333ea",
          bgColor: "#f3e8ff",
          icon: faUser,
        };
    }
  };

  const renderSentVia = (sentVias: string[] | undefined) => {
    if (!sentVias || sentVias.length === 0) return null;

    return (
      <div className="sent-via-icons">
        {sentVias.includes("email") && (
          <div className="sent-via-icon-wrapper email">
            <FontAwesomeIcon
              title="Email"
              icon={faEnvelope}
              className="sent-via-icon"
            />
          </div>
        )}
        {sentVias.includes("sms") && (
          <div className="sent-via-icon-wrapper sms">
            <FontAwesomeIcon
              icon={faMessage}
              title="SMS"
              className="sent-via-icon"
            />
          </div>
        )}
        {(sentVias.includes("app") || sentVias.includes("notification")) && (
          <div className="sent-via-icon-wrapper notification">
            <FontAwesomeIcon
              title="In-app"
              icon={faBell}
              className="sent-via-icon"
            />
          </div>
        )}
      </div>
    );
  };

  const handleCancel = useCallback(
    (invite: PendingActivity) => {
      onCancel?.(invite);
    },
    [onCancel]
  );

  const handleSendDM = useCallback(
    async (record: any) => {
      const { collabId, bookingId } = record;
      if (!collabId || !bookingId) {
        console.error("Missing collabId or bookingId");
        return;
      }
      try {
        const response: any = await collabStartChat({ collabId, bookingId });
        if (response?.chat?._id) {
          navigate(`/messenger/${response.chat._id}`);
        }
      } catch (error) {
        console.error("Error starting chat:", error);
      }
    },
    [navigate]
  );

  const columns: ColumnsType<T> = useMemo(
    () => [
      {
        title: "CREATOR",
        key: "creator",
        dataIndex: "creator",
        fixed: "left",
        width: 170,
        hidden: !showCreatorInfo,
        render: (_, record: any) => (
          <div className="creator-cell">
            <Avatar src={record.avatarUrl} size={40} className="creator-avatar">
              {record.name?.[0] || "C"}
            </Avatar>
            <div className="creator-info">
              <div className="creator-name" title={record.name}>
                {record.name || "Creator"}
              </div>
            </div>
          </div>
        ),
      },
      {
        title: "LOCATION",
        key: "venue",
        dataIndex: "venue",
        width: 200,
        render: (venue: any) => (
          <div className="location-cell">
            <FontAwesomeIcon icon={faMapMarkerAlt} className="location-icon" />
            <span>{venue?.addressDisplay || "-"}</span>
          </div>
        ),
      },
      {
        title: "INVITE TYPE",
        key: "inviteType",
        dataIndex: "inviteType",
        width: 120,
        render: (type: any) => {
          const config = getInviteTypeConfig(type ?? InviteType.Personal);
          return (
            <Tag
              className="invite-type-tag"
              style={{
                backgroundColor: config.bgColor,
                color: config.color,
              }}
            >
              <FontAwesomeIcon icon={config.icon} />
              {config.label}
            </Tag>
          );
        },
      },
      {
        title: "COMPENSATION",
        key: "compensation",
        width: 150,
        render: (_, record: any) => {
          const foodComp =
            record.foodCompAmount || record.compensation?.foodCompAmount;
          const cashComp =
            record.cashCompAmount || record.compensation?.cashCompAmount;

          if (!foodComp && !cashComp) return "-";

          return (
            <div className="compensation-cell">
              {foodComp && (
                <div className="compensation-row">
                  <FontAwesomeIcon
                    icon={faUtensils}
                    className="compensation-icon food-icon"
                  />
                  <span className="comp-value">${foodComp}</span>
                </div>
              )}
              {cashComp && (
                <div className="compensation-row">
                  <FontAwesomeIcon
                    icon={faDollarSign}
                    className="compensation-icon cash-icon"
                  />
                  <span className="comp-value">${cashComp}</span>
                </div>
              )}
            </div>
          );
        },
      },
      {
        title: "DATE & TIME",
        key: "dateTime",
        width: 100,
        render: (_, record: any) => {
          const date = record.requestedAt;

          if (!date) return "-";

          const formattedDate = dayjs.utc(date).format("MMM D, YYYY");
          const formattedHour = dayjs.utc(date).format("h:mm A");
          return (
            <div className="datetime-cell">
              <div className="datetime-date">{formattedDate}</div>
              {formattedHour && (
                <div className="datetime-time">{formattedHour}</div>
              )}
            </div>
          );
        },
      },
      {
        title: "STATUS",
        key: "status",
        dataIndex: "status",
        width: 100,
        render: (status: any) => {
          const config = getStatusConfig(status);
          return (
            <Tag
              className="status-tag"
              style={{
                backgroundColor: config.bgColor,
                color: config.color,
              }}
            >
              <FontAwesomeIcon icon={config.icon} />
              {config.label}
            </Tag>
          );
        },
      },
      {
        title: "SENT VIA",
        key: "sendVia",
        dataIndex: "sendVia",
        width: 140,
        render: (sentVias: any) => renderSentVia(sentVias),
      },
      {
        title: "ACTIONS",
        key: "actions",
        width: 80,
        render: (_, record: any) => {
          const menuItems = [];

          if (record.status === InviteStatusEnum.Sent) {
            menuItems.push({
              key: "cancel",
              label: "Cancel",
              danger: true,
              onClick: () => handleCancel(record),
            });
          }

          if (record.status === InviteStatusEnum.Booked) {
            menuItems.push({
              key: "sendDM",
              label: "Send DM",
              onClick: () => handleSendDM(record),
            });
          }

          return (
            <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
              <Button
                type="text"
                icon={<MoreOutlined />}
                className="actions-button"
              />
            </Dropdown>
          );
        },
      },
    ],
    [showCreatorInfo, handleCancel, handleSendDM]
  );

  return (
    <div className="invitations-table-wrapper">
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey={"_id"}
        loading={loading}
        scroll={scroll}
        pagination={paginationConfig}
        className={className}
      />
    </div>
  );
};

export default InvitationsTable;
